import React, { memo, useCallback } from 'react'
import { Box, useTheme } from '@mui/material'
import { LineType } from 'lightweight-charts'
import tinycolor from 'tinycolor2'
import ColorSelector from '../../../../colorSelector/colorSelector'
import { ChartLineStyle, clampLineThickness, LineThickness } from '../../../../colorSelector/lineStyle.types'
import { primarySecColorDarkTheme, primarySecColorLightTheme } from '../../../styles'
import OutputPlotTypes from './OutputPlotTypes'

export type MultiStrikeUnderlyingStyleChange = {
    underlyingColor?: string
    underlyingThickness?: number
    underlyingLineStyle?: ChartLineStyle
    underlyingLineType?: LineType
}

type Props = {
    underlyingColor?: string
    underlyingThickness?: number
    underlyingLineStyle?: ChartLineStyle
    underlyingLineType?: LineType
    onChange: (partial: MultiStrikeUnderlyingStyleChange) => void
}

const MultiStrikeUnderlyingStyle: React.FC<Props> = ({
    underlyingColor,
    underlyingThickness,
    underlyingLineStyle,
    underlyingLineType,
    onChange,
}) => {
    const theme = useTheme()
    const defaultColor = theme.palette.mode === 'dark' ? primarySecColorDarkTheme : primarySecColorLightTheme
    const color = underlyingColor ?? defaultColor
    const opacity = tinycolor(color).getAlpha()
    const lineThickness = clampLineThickness(underlyingThickness)
    const lineStyle = underlyingLineStyle ?? 'large_dashed'

    const handleColorChange = useCallback((clr: string, transparency: number) => {
        onChange({ underlyingColor: tinycolor(clr).setAlpha(transparency).toHex8String() })
    }, [onChange])

    const handleThicknessChange = useCallback((thickness: LineThickness) => {
        onChange({ underlyingThickness: thickness })
    }, [onChange])

    const handleLineStyleChange = useCallback((style: ChartLineStyle) => {
        onChange({ underlyingLineStyle: style })
    }, [onChange])

    const handleLineTypeChange = useCallback((lineType: LineType) => {
        onChange({ underlyingLineType: lineType })
    }, [onChange])

    return (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <ColorSelector
                compact
                showOnlySwatch
                color={color}
                opacity={opacity}
                lineThickness={lineThickness}
                lineStyle={lineStyle}
                onColorChange={handleColorChange}
                onLineThicknessChange={handleThicknessChange}
                onLineStyleChange={handleLineStyleChange}
            />
            <OutputPlotTypes lineType={underlyingLineType ?? LineType.Simple} onSelected={handleLineTypeChange} />
        </Box>
    )
}

export default memo(MultiStrikeUnderlyingStyle)
